import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";

const AdminTenantCtx = createContext(null);

export function AdminTenantProvider({ children }) {
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus, ctx.user]);

  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  const [myTenantKey, setMyTenantKey] = useState(undefined);
  const [adminTenantKey, setAdminTenantKey] = useState(() => localStorage.getItem("adminTenantKey") || "");

  // Load "my" tenant key (from token)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (authStatus !== "authenticated") {
        if (!cancelled) {
          setMyTenantKey(undefined);
          setIsPlatformAdmin(false);
        }
        return;
      }

      try {
        const session = await fetchAuthSession();

        const tk = session.tokens?.idToken?.payload?.["custom:tenantKey2"];

        const groups =
          session.tokens?.accessToken?.payload?.["cognito:groups"] ?? [];
        const admin =
          Array.isArray(groups) && groups.includes("platformAdmin");

        if (!cancelled) {
          setMyTenantKey(tk || undefined);
          setIsPlatformAdmin(admin);
        }
      } catch {
        if (!cancelled) {
          setMyTenantKey(undefined);
          setIsPlatformAdmin(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authStatus]);


  // For admins, default selection to their own tenant if nothing picked yet
  useEffect(() => {
    if (!isPlatformAdmin) return;
    if (!adminTenantKey && myTenantKey) setAdminTenantKey(myTenantKey);
  }, [isPlatformAdmin, myTenantKey, adminTenantKey]);

  // Persist selection
  useEffect(() => {
    if (!isPlatformAdmin) return;
    localStorage.setItem("adminTenantKey", adminTenantKey || "");
  }, [isPlatformAdmin, adminTenantKey]);

  const effectiveTenantKey = isPlatformAdmin ? (adminTenantKey || "") : myTenantKey;

  const value = useMemo(() => ({
    isPlatformAdmin,
    myTenantKey,
    adminTenantKey,
    setAdminTenantKey,
    tenantKey: effectiveTenantKey,
  }), [isPlatformAdmin, myTenantKey, adminTenantKey, effectiveTenantKey]);

  return <AdminTenantCtx.Provider value={value}>{children}</AdminTenantCtx.Provider>;
}

export function useAdminTenant() {
  const v = useContext(AdminTenantCtx);
  if (!v) throw new Error("useAdminTenant must be used within AdminTenantProvider");
  return v;
}
